// ── Safety filters ───────────────────────────────────────────
export const SAFETY_FILTER = {
	LOW: 'block_only_high',
	MEDIUM: 'block_medium_and_above',
	HIGH: 'block_low_and_above',
} as const

export type SafetyFilter = (typeof SAFETY_FILTER)[keyof typeof SAFETY_FILTER]

// ── Output formats ───────────────────────────────────────────
export const OUTPUT_FORMAT = {
	JPG: 'jpg',
	PNG: 'png',
	WEBP: 'webp',
} as const

export type OutputFormat = (typeof OUTPUT_FORMAT)[keyof typeof OUTPUT_FORMAT]

// ── Video reference types ────────────────────────────────────
export const VIDEO_REFERENCE_TYPE = {
	FEATURE: 'feature',
	BASE: 'base',
} as const

export type VideoReferenceType = (typeof VIDEO_REFERENCE_TYPE)[keyof typeof VIDEO_REFERENCE_TYPE]

// ── Character orientation ────────────────────────────────────
export const CHARACTER_ORIENTATION = {
	VIDEO: 'video',
	IMAGE: 'image',
} as const

export type CharacterOrientation =
	(typeof CHARACTER_ORIENTATION)[keyof typeof CHARACTER_ORIENTATION]

// ── Generation speed ─────────────────────────────────────────
export const SPEED = {
	TURBO: 'turbo',
	FAST: 'fast',
	RELAXED: 'relaxed',
} as const

export type Speed = (typeof SPEED)[keyof typeof SPEED]

// ── Providers ────────────────────────────────────────────────
export const PROVIDER = {
	MIDJOURNEY: 'midjourney',
	KLING: 'kling',
	FLUX: 'flux',
	NANO_BANANA: 'nano_banana',
} as const

// ── Backend model substrings ─────────────────────────────────
export const BACKEND_MODEL = {
	IMAGEN: 'imagen',
} as const

// ── Task types ───────────────────────────────────────────────
export const TASK_TYPE = {
	I2V: 'i2v',
	I2I: 'i2i',
} as const

// ── Pending generation localStorage key ─────────────────────
export const PENDING_GENERATION_KEY = 'pendingGeneration'
export const GENERATE_PROMPT_KEY = 'generatePrompt'
export const GENERATE_SETTINGS_KEY = 'generateSettings'
export const PERSISTED_UPLOADS_KEY = 'generatePersistedUploads'
export const UPLOADS_EXPIRY_MS = 60 * 60 * 1000 // 1 hour

/** Последний завершённый результат генерации (URL, обложки, метаданные).
 *  Хранит виджет-результат между перезагрузками страниц и навигацией —
 *  очищается только при явной очистке виджета пользователем («Очистить»). */
export const GENERATE_RESULT_KEY = 'generateResult_v1'

// ── Widget state на /bots/* страницах ──────────────────────
/** Мапа category → last-used model id. JSON в localStorage. */
export const WIDGET_LAST_MODEL_MAP_KEY = 'botsWidgetLastModelMap'
/** Глобальный флаг: виджет свёрнут до шапки. Хранится одним
 *  значением (не per-category) — как договорились на Этапе 0.
 *  Значения: 'true' / отсутствие ключа = раскрыт по умолчанию. */
export const BOTS_WIDGET_COLLAPSED_KEY = 'botsWidgetCollapsed'

// Свёрнутый бар /generate (паритет со сворачиванием виджета /bots): ключ
// СВОЙ — поверхности разные, свёрнутость одной не должна сюрпризить другую.
export const GENERATE_BAR_COLLAPSED_KEY = 'generateBarCollapsed'

// Компактный вид КАСТОМНОГО виджета инструмента (.gtm): уменьшение габаритов
// только по отдельной кнопке на самом виджете (владелец, 25.09), стандартный
// бар размеры не меняет. 'true' — компактно; отсутствие ключа — обычный вид.
export const GENERATE_TOOL_COMPACT_KEY = 'generateToolCompact'

// ── Create video ─────────────────────────────────────────────
export const VIDEO_MODEL_ID = 'kling-2.6/image-to-video'
export const DEFAULT_VIDEO_PROMPT =
	'Gentle natural movement, slow cinematic camera pull-back, soft atmospheric lighting, smooth and realistic animation'

// ── Polling config ───────────────────────────────────────────
// Потолка опроса нет (план «Ждём ответа нейросети», Ф2, 26.09): исход генерации решает ответ нейросети, сервер
// закрывает генерацию им сам. Прежние 240 опросов × 6 с объявляли «Таймаут генерации», пока ролик дорисовывался.
// Чем дольше идёт генерация, тем реже спрашиваем (pollDelayMs) — как сверка на сервере.
export const POLL_INTERVAL_MS = 6000

/** Пауза перед следующим вопросом серверу: первые 10 минут — POLL_INTERVAL_MS, до часа — 15 с, дальше — минута. */
export function pollDelayMs(elapsedMs: number): number {
	if (elapsedMs < 10 * 60 * 1000) return POLL_INTERVAL_MS
	if (elapsedMs < 60 * 60 * 1000) return 15_000
	return 60_000
}

// Запись о незавершённой генерации (восстановление после перезагрузки) живёт, пока сервер не ответил: у записи с
// номером генерации срока нет. Срок — только у записи без номера (перезагрузились раньше, чем ручка ответила):
// спросить о ней некого.
export const PENDING_TASK_EXPIRY_MS = 90 * 60 * 1000

/** Запись о генерации устарела: номера нет (спросить сервер не о чем) и прошло PENDING_TASK_EXPIRY_MS. */
export function isPendingExpired(task: { taskId?: string | null; startedAt?: number }, now: number = Date.now()): boolean {
	return !task.taskId && now - (task.startedAt ?? 0) > PENDING_TASK_EXPIRY_MS
}

// ── Upload limits ────────────────────────────────────────────
export const MAX_OMNI_REFS_WITH_VIDEO = 4
export const MAX_OMNI_REFS = 7
export const MAX_MULTI_SHOTS = 6
export const DEFAULT_VIDEO_DURATION = 5
export const MIN_VIDEO_DURATION_SLIDER = 3

// ── Image validation ─────────────────────────────────────────
export const IMAGE_MIN_PX = 340
export const IMAGE_MAX_PX = 3850
export const IMAGE_MAX_RATIO = 2.5
